

import logger from '../../base/media/logger';
import { safePlayMediaElement } from '../../base/media/playbackUtils';

import {
    CLEAR_INTERVAL,
    INTERVAL_TIMEOUT,
    SET_INTERVAL,
    timerWorkerScript
} from './TimeWorker';

const HAVE_CURRENT_DATA = 2;

/**
 * Represents a modified MediaStream that adds video as pip on a desktop stream.
 * <tt>MHStreamPresenterEffect</tt> does the processing of the original
 * desktop stream.
 */
/**
 * HTMLMediaElement.HAVE_CURRENT_DATA - the readyState at which a video first has a decoded frame that
 * canvas drawImage() can actually copy. Below this, drawImage paints nothing and reports nothing.
 */
export default class MHStreamPresenterEffect {
    _canvas: HTMLCanvasElement;
    _lastRenderErrorLog: number;
    _ctx: CanvasRenderingContext2D;
    _desktopElement: HTMLVideoElement;
    _desktopStream: MediaStream;
    _frameRate: number;
    _onVideoFrameTimer: Function;
    _onVideoFrameTimerWorker: Function;
    _renderVideo: Function;
    _videoFrameTimerWorker: Worker;
    _videoElement: HTMLVideoElement;
    isEnabled: Function;
    startEffect: Function;
    stopEffect: Function;

    /**
     * Represents a modified MediaStream that adds a camera track at the
     * bottom right corner of the desktop track using a HTML canvas.
     * <tt>MHStreamPresenterEffect</tt> does the processing of the original
     * video stream.
     *
     * @param {MediaStream} videoStream - The video stream which is user for
     * creating the canvas.
     */
    constructor(videoStream: MediaStream) {
        const videoDiv = document.createElement('div');
        const firstVideoTrack = videoStream.getVideoTracks()[0];
        const { height, width, frameRate } = firstVideoTrack.getSettings() ?? firstVideoTrack.getConstraints();

        this._canvas = document.createElement('canvas');
        this._ctx = this._canvas.getContext('2d');

        this._desktopElement = document.createElement('video');
        this._videoElement = document.createElement('video');
        videoDiv.appendChild(this._videoElement);
        videoDiv.appendChild(this._desktopElement);
        if (document.body !== null) {
            document.body.appendChild(videoDiv);
        }

        // Set the video element properties
        this._frameRate = parseInt(frameRate, 10);
        this._videoElement.width = parseInt(width, 10);
        this._videoElement.height = parseInt(height, 10);
        this._videoElement.autoplay = true;
        this._videoElement.srcObject = videoStream;

        // autoplay is not enough to start the video on Safari, it's fine to call play() on other platforms as well
        this._safePlay(this._videoElement, 'Presenter camera overlay');

        // set the style attribute of the div to make it invisible
        videoDiv.style.display = 'none';

        // Bind event handler so it is only bound once for every instance.
        this._onVideoFrameTimer = this._onVideoFrameTimer.bind(this);
    }

    /**
     * EventHandler onmessage for the videoFrameTimerWorker WebWorker.
     *
     * @private
     * @param {EventHandler} response - The onmessage EventHandler parameter.
     * @returns {void}
     */
    _onVideoFrameTimer(response) {
        if (response.data.id === INTERVAL_TIMEOUT) {
            this._renderVideo();
        }
    }

    /**
     * Loop function to render the video frame input and draw presenter effect.
     *
     * @private
     * @returns {void}
     */
    _renderVideo() {
        const [ track ] = this._desktopStream.getVideoTracks();

        // The desktop track can vanish from the stream while this interval keeps firing: the user hits the
        // browser's own "Stop sharing" bar, or the track is disposed during a device switch or a reconnect.
        // Destructuring then yields undefined and track.getSettings() threw on EVERY tick. Unlike the
        // virtual-background loop this one is SET_INTERVAL driven, so it survives the throw - which is
        // worse, not better: the interval keeps running while nothing is ever drawn again. The canvas goes
        // on handing out a captureStream that is still "live" but has stopped producing frames, i.e. a
        // screenshare frozen for every other participant, reported nowhere.
        if (!track) {
            this._warnThrottled('Presenter effect: the desktop track is gone, nothing left to composite');

            return;
        }

        const { height, width } = track.getSettings() ?? track.getConstraints();
        const nextWidth = parseInt(width, 10);
        const nextHeight = parseInt(height, 10);

        // A track that is ending reports no dimensions; parseInt(undefined) is NaN, and assigning NaN
        // collapses the canvas to 0x0 - which silently produces an empty stream rather than an error.
        if (!(nextWidth > 0) || !(nextHeight > 0)) {
            this._warnThrottled(`Presenter effect: desktop track reports no usable size (${width}x${height})`);

            return;
        }

        // Adjust the canvas size in case the shared window was resized - but ONLY when it actually
        // changed. Assigning to canvas.width/height resets the drawing surface and forces a
        // reallocation, and this ran unconditionally at the full frame rate for the entire share.
        if (this._canvas.width !== nextWidth || this._canvas.height !== nextHeight) {
            this._canvas.width = nextWidth;
            this._canvas.height = nextHeight;
        }

        // Do not composite while the desktop element has no decoded frame.
        //
        // drawImage() from a video below HAVE_CURRENT_DATA is a silent no-op - it paints nothing and
        // throws nothing. The camera overlay on the next line still paints, so the canvas becomes
        // "black rectangle + camera", and THAT is what captureStream relays to the conference: remote
        // participants see the sharer's camera on a black background where the shared screen should be,
        // which is exactly the reported symptom. Skipping the whole frame keeps the last good composite
        // on the wire instead of publishing a corrupted one, and gives the element a chance to recover.
        if (this._desktopElement.readyState < HAVE_CURRENT_DATA) {
            this._warnThrottled('Presenter effect: desktop element has no frame yet'
                + ` (readyState=${this._desktopElement.readyState}); retrying playback`);

            // The element can be left paused by an autoplay-policy block or by the browser suspending a
            // hidden video; asking it to play again is cheap and is what actually recovers it.
            this._safePlay(this._desktopElement, 'Presenter desktop stream');

            return;
        }

        try {
            this._ctx.drawImage(this._desktopElement, 0, 0, this._canvas.width, this._canvas.height);
            this._ctx.drawImage(this._videoElement, this._canvas.width - this._videoElement.width,
                this._canvas.height - this._videoElement.height,
                this._videoElement.width, this._videoElement.height);

            // draw a border around the video element.
            this._ctx.beginPath();
            this._ctx.lineWidth = 2;
            this._ctx.strokeStyle = '#A9A9A9'; // dark grey
            this._ctx.rect(this._canvas.width - this._videoElement.width,
                this._canvas.height - this._videoElement.height,
                this._videoElement.width, this._videoElement.height);
            this._ctx.stroke();
        } catch (error) {
            this._warnThrottled('Presenter effect frame failed; dropping it and continuing', error);
        }
    }

    /**
     * Logs at most one warning per 10s.
     *
     * Every caller sits inside the render loop, which runs at the presenter frame rate - an unthrottled
     * warning there would emit continuously for as long as the condition lasts. These paths all leave the
     * canvas unpainted, so without a warning the share simply freezes with nothing reported anywhere,
     * which is precisely what made the original hang undiagnosable.
     *
     * @param {string} message - What went wrong.
     * @param {Error} [error] - Optional underlying error.
     * @private
     * @returns {void}
     */
    _warnThrottled(message, error) {
        const now = Date.now();

        if (!this._lastRenderErrorLog || now - this._lastRenderErrorLog > 10000) {
            this._lastRenderErrorLog = now;
            error ? logger.warn(message, error) : logger.warn(message);
        }
    }

    /**
     * Checks if the local track supports this effect.
     *
     * @param {MHLocalTrack} mHLocalTrack - Track to apply effect.
     * @returns {boolean} - Returns true if this effect can run on the
     * specified track, false otherwise.
     */
    isEnabled(mHLocalTrack: Object) {
        return mHLocalTrack.isVideoTrack() && mHLocalTrack.videoType === 'desktop';
    }

    /**
     * Starts loop to capture video frame and render presenter effect.
     *
     * @param {MediaStream} desktopStream - Stream to be used for processing.
     * @returns {MediaStream} - The stream with the applied effect.
     */
    startEffect(desktopStream: MediaStream) {
        const firstVideoTrack = desktopStream.getVideoTracks()[0];
        const { height, width } = firstVideoTrack.getSettings() ?? firstVideoTrack.getConstraints();

        // set the desktop element properties.
        this._desktopStream = desktopStream;
        this._desktopElement.width = parseInt(width, 10);
        this._desktopElement.height = parseInt(height, 10);
        this._desktopElement.autoplay = true;
        this._desktopElement.srcObject = desktopStream;

        // autoplay is not enough to start the video on Safari, it's fine to call play() on other platforms as well
        this._safePlay(this._desktopElement, 'Presenter desktop stream');

        this._canvas.width = parseInt(width, 10);
        this._canvas.height = parseInt(height, 10);
        this._videoFrameTimerWorker = new Worker(timerWorkerScript, { name: 'Presenter effect worker' });
        this._videoFrameTimerWorker.onmessage = this._onVideoFrameTimer;
        this._videoFrameTimerWorker.postMessage({
            id: SET_INTERVAL,
            timeMs: 1000 / this._frameRate
        });

        return this._canvas.captureStream(this._frameRate);
    }

    /**
     * Stops the capture and render loop.
     *
     * @returns {void}
     */
    stopEffect() {
        this._videoFrameTimerWorker.postMessage({
            id: CLEAR_INTERVAL
        });
        this._videoFrameTimerWorker.terminate();
    }

    /**
     * Plays a video element safely and classifies expected browser play errors.
     *
     * @param {HTMLVideoElement} element - The video element to play.
     * @param {string} context - Log context for diagnostics.
     * @private
     * @returns {void}
     */
    _safePlay(element, context) {
        if (!element || !element.isConnected) {
            return;
        }

        safePlayMediaElement(element, {
            onAutoplayBlocked: error => {
                logger.warn(`${context} play blocked by browser policy:`, error);
            },
            onError: error => {
                logger.error(`${context} play failed:`, error);
            }
        });
    }

}
